import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pathsAPI } from '../api/pathsAPI';
import {
  MY_PATH_VIEW,
  PATH_PARAM,
  PLAN_PARAM,
  VIEW_PARAM,
  namesPathsSurface,
  parsePathsChatParams,
} from '../utils/pathsChatUrl';
import { readPanelOpen, writePanelOpen } from '../utils/panelOpenStorage';
import { displayText } from '../utils/displayText';
import { bindPendingEmptyStateLine, shouldShowPlanningEmptyState } from '../components/planningEmptyState';

// Everything Paths does inside /chat: which layout shows, the planning or
// revision conversation, draft generation, accept and discard. ChatAgentPanel
// mounts this and routes its composer here while a Paths session is open;
// it owns no Paths state itself.
//
// The URL is the source of truth (see utils/pathsChatUrl.js), so a path view
// can be linked and survives a reload:
//   ?path=P          plan only
//   ?path=P&plan=S   split: the path's conversation beside its panel
//   ?plan=S          a planning thread
//   ?plan=new        create a session once, then replace the URL with its id
//
// All network calls go through pathsAPI; none of them builds a URL for the
// student to follow.

export const GENERATION_PHASE_LABELS = {
  understanding: 'Understanding your goal…',
  drafting: 'Drafting your plan…',
  validating: 'Checking the plan…',
  saving: 'Saving…',
};

export const GENERATION_STALE_MESSAGE = 'This is taking longer than usual.';

// A generation's last known outcome, for the notice and the retry button:
// null, 'failed' (it finished without a draft) or 'stale' (the server says
// it is past the point it could finish).
const noticeFor = (generation) => {
  if (!generation || typeof generation !== 'object') return null;
  if (generation.status === 'failed' || generation.status === 'invalid_output') return 'failed';
  if (generation.status === 'in_progress' && generation.stale) return 'stale';
  return null;
};

// sessionStorage key holding a session created for "?plan=new" until the URL
// carries its id, so a reload in that gap reuses it instead of creating a
// second one. The backend also reuses an empty open creation session.
export const PENDING_NEW_PLAN_KEY = 'paths-plan-new';

function readPendingNewPlan() {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(PENDING_NEW_PLAN_KEY) || 'null');
    return parsed && typeof parsed.key === 'string' && typeof parsed.id === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

function writePendingNewPlan(value) {
  try {
    if (value) window.sessionStorage.setItem(PENDING_NEW_PLAN_KEY, JSON.stringify(value));
    else window.sessionStorage.removeItem(PENDING_NEW_PLAN_KEY);
  } catch {
    // Storage unavailable: the backend's empty-session reuse still applies.
  }
}

const isPending = (m) => String(m?.id || '').startsWith('pending-');

export default function usePathsChat({
  enabled = true,
  chatReady = true,
  chatSessionId = null,
  setComposerText,
  focusComposer,
  onShowMyPath,
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => parsePathsChatParams(searchParams), [searchParams]);
  const active = Boolean(enabled) && namesPathsSurface(params);
  const { pathId, planSessionId, newPlan } = params;

  const [session, setSession] = useState(null);
  const [sessionState, setSessionState] = useState('idle'); // idle | loading | ready | unavailable
  const [messages, setMessages] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [draftPath, setDraftPath] = useState(null);
  const [readyToGenerate, setReadyToGenerate] = useState(false);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationPhase, setGenerationPhase] = useState(null);
  const [generationNotice, setGenerationNotice] = useState(null);
  const [generationStartedAt, setGenerationStartedAt] = useState(null);
  const [serverNow, setServerNow] = useState(null);
  const [pathTitle, setPathTitle] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [askingForChange, setAskingForChange] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [pathReloadKey, setPathReloadKey] = useState(0);
  const [pathsChangedKey, setPathsChangedKey] = useState(0);
  const [panelOpen, setPanelOpenState] = useState(() => readPanelOpen(true));
  // The session this hook just created: its first load is not a "resume",
  // so the empty-state line stays up instead of flashing away.
  const [justCreatedId, setJustCreatedId] = useState(null);
  const createdForRef = useRef(null);
  const loadRequestRef = useRef(0);
  const abortRef = useRef(null);
  // The generation being followed (started here, or resumed after a
  // reload): aborted on unmount and when the open planning session
  // changes. Hiding Paths while Apps or Cases is showing does not abort
  // it. followedAttemptRef stops a reload of the same session resuming
  // the same attempt twice.
  const generationAbortRef = useRef(null);
  const followedAttemptRef = useRef(null);
  const followGenerationRef = useRef(null);

  const layout = !active ? 'none'
    : pathId && (planSessionId || newPlan) ? 'split'
      : pathId ? 'plan'
        : 'thread';

  // persist is for a choice the student made (open, close, draft, accept).
  // A load that finds a draft or a running attempt opens the panel for this
  // visit and leaves the saved choice alone.
  const setPanelOpen = useCallback((open, { persist = true } = {}) => {
    setPanelOpenState(open);
    if (persist) writePanelOpen(open);
  }, []);

  const navigateTo = useCallback((next, { replace = false } = {}) => {
    setSearchParams(() => {
      const out = new URLSearchParams();
      if (next.pathId) out.set(PATH_PARAM, next.pathId);
      if (next.planSessionId) out.set(PLAN_PARAM, next.planSessionId);
      if (next.myPath) out.set(VIEW_PARAM, MY_PATH_VIEW);
      return out;
    }, { replace });
  }, [setSearchParams]);

  const notifyPathsChanged = useCallback(() => setPathsChangedKey((k) => k + 1), []);

  // Opening a path always opens its panel (decision 7). Disabled while
  // Apps or Cases is showing, and a return to the same path does not
  // count as opening it again, so a panel the student closed stays closed.
  const panelOpenedForRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    if (!pathId) {
      panelOpenedForRef.current = null;
      return;
    }
    if (panelOpenedForRef.current === pathId) return;
    panelOpenedForRef.current = pathId;
    setPanelOpen(true);
  }, [enabled, pathId, setPanelOpen]);

  // The accepted path's title, for a revision panel before the new draft
  // has a title of its own. A creation chat has no path yet.
  useEffect(() => {
    // Pausing Paths (another tab) must not wipe the title. A chat with no
    // path, while Paths is enabled, still clears it.
    if (!enabled) return undefined;
    if (!pathId) {
      setPathTitle(null);
      return undefined;
    }
    let cancelled = false;
    pathsAPI.getPath(pathId)
      .then((p) => { if (!cancelled) setPathTitle(displayText(p?.title) || null); })
      .catch(() => { if (!cancelled) setPathTitle(null); });
    return () => { cancelled = true; };
  }, [enabled, pathId, pathReloadKey]);

  // ?view=my-path: open the sidebar on the folder, then drop the flag so a
  // reload does not keep reopening it.
  useEffect(() => {
    if (!enabled || !params.myPath) return;
    onShowMyPath?.();
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      out.delete(VIEW_PARAM);
      return out;
    }, { replace: true });
  }, [enabled, params.myPath, onShowMyPath, setSearchParams]);

  // Leaving Paths for an ordinary chat: ChatAgentPanel's own session changed
  // after its bootstrap, so the student picked a chat or started one.
  const baselineChatSessionRef = useRef(undefined);
  useEffect(() => {
    if (!chatReady) return;
    if (baselineChatSessionRef.current === undefined) {
      baselineChatSessionRef.current = chatSessionId;
      return;
    }
    if (chatSessionId === baselineChatSessionRef.current) return;
    baselineChatSessionRef.current = chatSessionId;
    if (active) navigateTo({});
  }, [chatReady, chatSessionId, active, navigateTo]);

  // ?plan=new (optionally with ?path=P, a revision): create once, then
  // replace the URL with the new session's id. The ref, not effect cleanup,
  // guards the create: under StrictMode the effect runs twice, and the
  // first run's request must still navigate when it lands.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!enabled || !newPlan) return;
    const key = pathId ? `revision:${pathId}` : 'creation';
    const stored = readPendingNewPlan();
    if (stored && stored.key === key) {
      writePendingNewPlan(null);
      navigateTo({ pathId, planSessionId: stored.id }, { replace: true });
      return;
    }
    if (createdForRef.current === key) return;
    createdForRef.current = key;
    setCreating(true);
    setError(null);
    pathsAPI.createSession(pathId ? 'revision' : 'creation', pathId || null)
      .then((created) => {
        if (!created?.id) throw new Error('no session id');
        // Held until the URL carries the id: a reload in between reuses it.
        writePendingNewPlan({ key, id: created.id });
        bindPendingEmptyStateLine(created.id);
        if (!mountedRef.current) return;
        setJustCreatedId(created.id);
        navigateTo({ pathId, planSessionId: created.id }, { replace: true });
        writePendingNewPlan(null);
        notifyPathsChanged();
      })
      .catch(() => {
        if (mountedRef.current) setError('A new plan could not be started. Please try again.');
      })
      .finally(() => {
        createdForRef.current = null;
        if (mountedRef.current) setCreating(false);
      });
  }, [enabled, newPlan, pathId, navigateTo, notifyPathsChanged]);

  const loadSession = useCallback(async (id, { quiet = false, resume = true } = {}) => {
    const request = ++loadRequestRef.current;
    if (!quiet) setSessionState('loading');
    try {
      const data = await pathsAPI.getSession(id);
      if (request !== loadRequestRef.current) return null;
      if (!data || typeof data !== 'object' || !data.id) throw new Error('malformed session');
      setSession(data);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setReadyToGenerate(data.state === 'ready_to_generate');
      const nextProposal = data.proposal?.version ? data.proposal : null;
      setProposal(nextProposal);
      setSessionState('ready');
      const generation = data.generation && typeof data.generation === 'object' ? data.generation : null;
      if (generation?.started_at) setGenerationStartedAt(generation.started_at);
      setServerNow(generation?.server_now || null);
      const notice = noticeFor(generation);
      if (notice) {
        setGenerationNotice(notice);
      } else if (resume && generation?.status === 'in_progress' && generation.attempt_id
        && followedAttemptRef.current !== generation.attempt_id) {
        // A reload mid-generation: follow the running attempt, never start one.
        setPanelOpen(true, { persist: false });
        followedAttemptRef.current = generation.attempt_id;
        followGenerationRef.current?.({
          sessionId: data.id,
          phase: generation.phase,
          startedAt: generation.started_at,
          run: (callbacks, signal) => pathsAPI.resumeGeneration(data.id, callbacks, signal),
        });
      }
      if (nextProposal) setPanelOpen(true, { persist: false });
      if (data.path_id) {
        pathsAPI.getPath(data.path_id)
          .then((p) => { if (request === loadRequestRef.current) setDraftPath(p); })
          .catch(() => { if (request === loadRequestRef.current) setDraftPath(null); });
      } else {
        setDraftPath(null);
      }
      return data;
    } catch {
      if (request === loadRequestRef.current) {
        setSession(null);
        setMessages([]);
        setProposal(null);
        setSessionState('unavailable');
      }
      return null;
    }
  }, [setPanelOpen]);

  // While Paths is paused the address bar no longer carries ?plan= (the
  // tab navigated to /apps or /cases). Keep the session id from the last
  // enabled render so that drop is not "the student left this plan".
  // Leaving the tab therefore does not abort the generation or clear the
  // transcript, and coming back to the same id does not load it again.
  // myPath, newPlan and the panel-open effect above stay on the live
  // params and on `enabled`, so they still do not run on Apps or Cases.
  const paramsWhileEnabledRef = useRef(params);
  if (enabled) paramsWhileEnabledRef.current = params;
  const sessionPlanId = (enabled ? params : paramsWhileEnabledRef.current).planSessionId;
  const loadedPlanIdRef = useRef(undefined);

  useEffect(() => {
    if (!enabled) return;
    if (loadedPlanIdRef.current === sessionPlanId) return;

    abortRef.current?.abort();
    generationAbortRef.current?.abort();
    generationAbortRef.current = null;
    followedAttemptRef.current = null;
    setError(null);
    setGenerating(false);
    setGenerationPhase(null);
    setGenerationNotice(null);
    setGenerationStartedAt(null);
    setServerNow(null);
    setSending(false);
    setAskingForChange(false);
    loadedPlanIdRef.current = sessionPlanId;
    if (!sessionPlanId) {
      loadRequestRef.current += 1;
      setSession(null);
      setMessages([]);
      setProposal(null);
      setDraftPath(null);
      setReadyToGenerate(false);
      setSessionState('idle');
      return;
    }
    loadSession(sessionPlanId);
  }, [enabled, sessionPlanId, loadSession]);

  useEffect(() => () => {
    abortRef.current?.abort();
    generationAbortRef.current?.abort();
  }, []);

  // Once a creation draft is accepted the path exists and has no pending
  // draft: carrying on the conversation is a revision, so later turns go to
  // the path's revision session (the backend reuses one open session per
  // path, and the transcript spans every session about the path).
  const ensureSendTarget = useCallback(async () => {
    if (!session) return null;
    if (session.purpose !== 'creation' || !session.path_id || proposal) return session.id;
    const revision = await pathsAPI.createSession('revision', session.path_id);
    if (!revision?.id) throw new Error('no session id');
    navigateTo({ pathId: session.path_id, planSessionId: revision.id }, { replace: true });
    return revision.id;
  }, [session, proposal, navigateTo]);

  const send = useCallback(async (rawText) => {
    const text = typeof rawText === 'string' ? rawText.trim() : '';
    if (!text || sending || !session || sessionState !== 'ready') return false;
    setSending(true);
    setError(null);
    const stamp = Date.now();
    const userId = `pending-user-${stamp}`;
    const botId = `pending-bot-${stamp}`;
    setMessages((prev) => [...prev, { id: userId, sender: 'user', message: text }]);
    const controller = new AbortController();
    abortRef.current = controller;
    let streamed = '';
    try {
      const target = await ensureSendTarget();
      await pathsAPI.postMessageStream(target, text, {
        onToken: (delta) => {
          if (typeof delta !== 'string') return;
          streamed += delta;
          setMessages((prev) => {
            const rest = prev.filter((m) => m.id !== botId);
            return [...rest, { id: botId, sender: 'bot', message: streamed }];
          });
        },
        onDone: (data) => {
          const fresh = Array.isArray(data?.messages) ? data.messages : [];
          setMessages((prev) => [...prev.filter((m) => !isPending(m)), ...fresh]);
          setReadyToGenerate(Boolean(data?.ready_to_generate));
          if (data?.brief && typeof data.brief === 'object') {
            setSession((current) => (current ? { ...current, brief: data.brief } : current));
          }
          setAskingForChange(false);
        },
      }, controller.signal);
      return true;
    } catch (e) {
      if (e?.name === 'AbortError') return false;
      setMessages((prev) => prev.filter((m) => !isPending(m)));
      setError('Your message was not sent. It is back in the box so you can try again.');
      setComposerText?.((current) => (current ? current : text));
      return false;
    } finally {
      setSending(false);
    }
  }, [sending, session, sessionState, ensureSendTarget, setComposerText]);

  // Follows one generation to its end: a new one (generate) or one already
  // running when the session loaded (loadSession). On success the session
  // reloads, which brings the draft card and the planner's "I've drafted"
  // line. An abort (unmount, another session) ends it silently.
  const followGeneration = useCallback(async ({ sessionId, phase, startedAt, run }) => {
    generationAbortRef.current?.abort();
    const controller = new AbortController();
    generationAbortRef.current = controller;
    setGenerating(true);
    setGenerationPhase(phase || 'understanding');
    setGenerationNotice(null);
    setError(null);
    setGenerationStartedAt(startedAt || new Date().toISOString());
    if (!startedAt) setServerNow(null);
    let stale = false;
    try {
      const result = await run({
        onPhase: (next) => { if (!controller.signal.aborted) setGenerationPhase(next); },
        onStale: () => { stale = true; },
        onStarted: () => { if (!controller.signal.aborted) notifyPathsChanged(); },
        onStatus: (status) => {
          if (controller.signal.aborted) return;
          if (status?.started_at) setGenerationStartedAt(status.started_at);
          if (status?.server_now) setServerNow(status.server_now);
        },
      }, controller.signal);
      if (controller.signal.aborted) return;
      if (stale || result?.stale) {
        setGenerationNotice('stale');
        notifyPathsChanged();
        return;
      }
      await loadSession(sessionId, { quiet: true });
      notifyPathsChanged();
    } catch (e) {
      if (e?.name === 'AbortError' || controller.signal.aborted) return;
      setGenerationNotice('failed');
      notifyPathsChanged();
      await loadSession(sessionId, { quiet: true, resume: false });
    } finally {
      if (generationAbortRef.current === controller) {
        generationAbortRef.current = null;
        setGenerating(false);
        setGenerationPhase(null);
      }
    }
  }, [loadSession, notifyPathsChanged]);
  followGenerationRef.current = followGeneration;

  const markRunKind = useCallback((kind) => {
    setSession((current) => (current ? {
      ...current,
      generation: { ...(current.generation || {}), kind, status: 'in_progress', stale: false },
    } : current));
  }, []);

  const generate = useCallback(async () => {
    if (!session || generating) return;
    const target = session;
    setPanelOpen(true);
    markRunKind(target.purpose === 'revision' ? 'revision' : 'generation');
    await followGeneration({
      sessionId: target.id,
      phase: 'understanding',
      run: (callbacks, signal) => (target.purpose === 'revision'
        ? pathsAPI.generateRevision(target.path_id, target.id, callbacks, signal)
        : pathsAPI.generate(target.id, callbacks, signal)),
    });
  }, [session, generating, followGeneration, setPanelOpen, markRunKind]);

  const updateDraft = useCallback(async () => {
    if (!session || generating) return;
    setPanelOpen(true);
    markRunKind('update');
    await followGeneration({
      sessionId: session.id,
      phase: 'understanding',
      run: (callbacks, signal) => pathsAPI.updateDraft(session.id, callbacks, signal),
    });
  }, [session, generating, followGeneration, setPanelOpen, markRunKind]);

  const accept = useCallback(async () => {
    const version = proposal?.version;
    const targetPathId = session?.path_id || version?.path_id;
    if (!version || !targetPathId || accepting) return;
    const waiting = Array.isArray(session?.brief?.pending_changes) ? session.brief.pending_changes.length : 0;
    if (waiting > 0 && session?.brief?.ready_to_update) {
      const confirmed = window.confirm(`Accept without your ${waiting} changes?`);
      if (!confirmed) return;
    }
    setAccepting(true);
    setError(null);
    try {
      await pathsAPI.acceptVersion(targetPathId, version.version_number);
      setProposal(null);
      setReadyToGenerate(false);
      setPathReloadKey((k) => k + 1);
      notifyPathsChanged();
      setPanelOpen(true);
      if (pathId !== targetPathId) navigateTo({ pathId: targetPathId, planSessionId: session.id });
      await loadSession(session.id, { quiet: true });
    } catch {
      setError('The plan was not accepted. Please try again.');
    } finally {
      setAccepting(false);
    }
  }, [proposal, session, accepting, pathId, navigateTo, loadSession, notifyPathsChanged, setPanelOpen]);

  const discard = useCallback(async () => {
    if (!session) return;
    setError(null);
    try {
      if (session.purpose === 'revision' && proposal?.version) {
        await pathsAPI.discardVersion(session.path_id, proposal.version.version_number);
        await loadSession(session.id, { quiet: true });
      } else {
        await pathsAPI.discardSession(session.id);
        navigateTo({ myPath: true });
      }
      notifyPathsChanged();
    } catch {
      setError('The draft was not discarded. Please try again.');
    }
  }, [session, proposal, loadSession, navigateTo, notifyPathsChanged]);

  const prefill = useCallback((text) => {
    if (!text) {
      focusComposer?.();
      return;
    }
    setComposerText?.((current) => (current && current.trim() ? `${current}\n\n${text}` : text));
    focusComposer?.();
  }, [setComposerText, focusComposer]);

  // "Discuss in chat", "Revise", "Ask for a replacement", "Revise plan": the
  // path's revision session in the split view, composer prefilled. Nothing is
  // sent until the student sends it.
  const discuss = useCallback(async (prefillText) => {
    if (!pathId) return;
    setPanelOpen(true);
    if (session && session.path_id === pathId && session.purpose === 'revision' && planSessionId === session.id) {
      prefill(prefillText);
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const revision = await pathsAPI.createSession('revision', pathId);
      if (!revision?.id) throw new Error('no session id');
      navigateTo({ pathId, planSessionId: revision.id });
      prefill(prefillText);
    } catch {
      setError('The conversation about this plan could not be opened. Please try again.');
    } finally {
      setCreating(false);
    }
  }, [pathId, session, planSessionId, navigateTo, prefill, setPanelOpen]);

  const openPath = useCallback((id) => navigateTo({ pathId: id }), [navigateTo]);
  const openPlanning = useCallback((id) => navigateTo({ planSessionId: id }), [navigateTo]);

  // "New path": create first, then navigate to the id, so this URL never
  // carries plan=new (only the old /paths/plan redirect does).
  const startNewPlan = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const created = await pathsAPI.createSession('creation');
      if (!created?.id) throw new Error('no session id');
      bindPendingEmptyStateLine(created.id);
      setJustCreatedId(created.id);
      navigateTo({ planSessionId: created.id });
      notifyPathsChanged();
    } catch {
      setError('A new plan could not be started. Please try again.');
    } finally {
      setCreating(false);
    }
  }, [creating, navigateTo, notifyPathsChanged]);

  // Leave Paths for the ordinary chat. A no-op when Paths is not showing,
  // so picking a chat never adds a history entry for nothing.
  const exit = useCallback(() => {
    if (active) navigateTo({});
  }, [active, navigateTo]);
  const closePanel = useCallback(() => setPanelOpen(false), [setPanelOpen]);
  const openPanel = useCallback(() => setPanelOpen(true), [setPanelOpen]);

  // Same gate PathsThread used to compute locally. ChatAgentPanel reads it
  // so an empty planning thread can use the ordinary empty-chat layout
  // (greeting and composer centred together) without turning that layout
  // on for a planning thread that already has messages.
  const resumePending = sessionState === 'loading' && planSessionId !== justCreatedId;
  const showPlanningEmpty = active
    && layout === 'thread'
    && session?.purpose !== 'revision'
    && shouldShowPlanningEmptyState({
      messages,
      error,
      generating,
      resumeLoading: resumePending,
    });

  return {
    active,
    layout,
    threadActive: layout === 'split' || layout === 'thread',
    pathId,
    planSessionId,
    session,
    sessionState,
    messages,
    proposal,
    draftPath,
    readyToGenerate,
    readyToUpdate: Boolean(session?.brief?.ready_to_update)
      && Array.isArray(session?.brief?.pending_changes)
      && session.brief.pending_changes.length > 0,
    pendingChanges: Array.isArray(session?.brief?.pending_changes) ? session.brief.pending_changes : [],
    nothingApplied: Boolean(session?.generation?.nothing_applied) && !generating && !generationNotice,
    updating: session?.generation?.kind === 'update',
    baseline: proposal?.version
      ? { version: proposal.version, items: proposal.items }
      : (draftPath?.current_version ? { version: draftPath.current_version, items: draftPath.items } : null),
    placeholder: askingForChange
      ? 'What should change?'
      : (session?.brief?.ready_to_update && (session?.brief?.pending_changes || []).length
        ? "Anything else to change?"
        : (proposal ? 'Ask for a change to your draft' : "Tell me what you're planning for")),
    lockReason: generating && (session?.generation?.kind === 'update' || session?.purpose === 'revision')
      ? 'Wait for the update to finish'
      : 'Wait for the draft to finish',
    sending,
    generating,
    generationPhase,
    generationNotice,
    generationStartedAt,
    serverNow,
    generationTitle: displayText(draftPath?.title) || pathTitle || 'New Path',
    accepting,
    creating,
    error,
    pathReloadKey,
    pathsChangedKey,
    panelOpen,
    justCreatedId,
    showPlanningEmpty,
    composerReady: active && layout !== 'plan' && Boolean(planSessionId) && sessionState === 'ready'
      && !sending && !creating && !generating,
    send,
    generate,
    updateDraft,
    accept,
    discard,
    requestChanges: () => {
      setAskingForChange(true);
      focusComposer?.();
    },
    discuss,
    openPath,
    openPlanning,
    startNewPlan,
    exit,
    closePanel,
    openPanel,
    notifyPathsChanged,
  };
}
